import {
  useFieldArray,
  Control,
  FieldValues,
  Controller,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "components/button/Button";
import { FaPlus, FaTrash } from "react-icons/fa";
import SmartSelect from "components/select/Select";
import Label from "components/label/Label";
import { useLazyGetUsersQuery } from "reduxFiles/api/userApi";
import {
  useGetIndustriesQuery,
  useGetProvincesQuery,
} from "reduxFiles/api/companyApi";
import { useMemo } from "react";
import { User } from "types/userTypes";

interface UserFieldArrayProps {
  control: Control<FieldValues>;
  name: string;
  error?: any;
}

const UserFieldArray = ({ control, name, error }: UserFieldArrayProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const [triggerUsers] = useLazyGetUsersQuery();

  const { data: provincesData } = useGetProvincesQuery();
  const { data: industrialsData } = useGetIndustriesQuery();

  const finalProvinceOptions = useMemo(
    () =>
      provincesData?.map((opt) => ({
        label: opt.name,
        value: opt.id,
      })) ?? [],
    [provincesData]
  );

  const finalIndustrialOptions = useMemo(
    () =>
      industrialsData?.map((opt) => ({
        label: `${opt.index} - ${t(`industrySectors.${opt.key}`)}`,
        value: opt.id,
        key: opt.key,
      })) ?? [],
    [industrialsData, t]
  );

  const searchUsers = async (text: string) => {
    try {
      const res = await triggerUsers({
        page: 1,
        limit: 50,
        filters: { search: text },
      }).unwrap();

      return res.items.map((u: User) => ({
        label: u.fullname + " " + u.mobile || "-",
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  return (
    <div className="col-span-1 md:col-span-2">
      <div className="mt-2 space-y-4">
        {fields.map((f, index) => (
          <div
            key={f.id}
            className="relative rounded-lg border bg-gray-50 p-4 dark:bg-gray-800/30"
          >
            <button
              type="button"
              onClick={() => remove(index)}
              className="absolute end-2 top-2 p-1 text-red-500 transition-colors hover:text-red-700"
            >
              <FaTrash size={14} />
            </button>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("organization.user")}
                </Label>
                <Controller
                  name={`${name}.${index}.userId`}
                  control={control}
                  rules={{
                    required: t("validation.fieldRequired", {
                      name: t("organization.user"),
                    }),
                  }}
                  render={({ field, fieldState: { error: fieldError } }) => (
                    <SmartSelect
                      mode="dynamic"
                      value={field.value}
                      onChange={field.onChange}
                      onSearch={searchUsers}
                      placeholder={t("actions.choose")}
                      error={fieldError}
                      defaultValue={f}
                    />
                  )}
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("organization.provinceIds")}
                </Label>
                <Controller
                  name={`${name}.${index}.provinceIds`}
                  control={control}
                  render={({ field, fieldState: { error: fieldError } }) => (
                    <SmartSelect
                      mode="static"
                      options={finalProvinceOptions}
                      value={field.value || []}
                      onChange={field.onChange}
                      multiple={true}
                      placeholder={t("actions.choose")}
                      error={fieldError}
                    />
                  )}
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("organization.industrialIds")}
                </Label>
                <Controller
                  name={`${name}.${index}.industrialIds`}
                  control={control}
                  render={({ field, fieldState: { error: fieldError } }) => (
                    <SmartSelect
                      mode="static"
                      options={finalIndustrialOptions}
                      value={field.value || []}
                      onChange={field.onChange}
                      multiple={true}
                      placeholder={t("actions.choose")}
                      error={fieldError}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            append({ userId: "", provinceIds: [], industrialIds: [] })
          }
          startIcon={<FaPlus size={14} />}
        >
          {t("organization.addUser")}
        </Button>
      </div>

      {error && <p className="mt-1 text-xs text-red-500">{error.message}</p>}
    </div>
  );
};

export default UserFieldArray;
